"use client";

import { useState, useEffect, useCallback, useRef, Children } from "react";
import { useTranslations } from "next-intl";
import * as m from "motion/react-m";

const CYCLE_INTERVAL = 5000;

export function MobileSocialProofCarousel({ children }: { children: React.ReactNode }) {
  const t = useTranslations("ClientKeys.contact_sales_page");
  const screens = Children.toArray(children);
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideStartedAt, setSlideStartedAt] = useState(() => Date.now());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const restartProgress = useCallback(() => {
    setSlideStartedAt(Date.now());
  }, []);

  const startTimer = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    restartProgress();
    intervalRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % screens.length);
      setSlideStartedAt(Date.now());
    }, CYCLE_INTERVAL);
  }, [restartProgress, screens.length]);

  useEffect(() => {
    startTimer();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startTimer]);

  const goToScreen = (index: number) => {
    setActiveIndex(index);
    startTimer();
  };

  return (
    <div className='lg:tw-hidden'>
      <div className='tw-relative tw-overflow-hidden'>
        {screens.map((screen, index) => (
          <m.div
            key={index}
            initial={{ opacity: index === 0 ? 1 : 0, x: index === 0 ? 0 : 40 }}
            animate={{
              opacity: activeIndex === index ? 1 : 0,
              x: activeIndex === index ? 0 : activeIndex > index ? -40 : 40,
            }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className={
              index === 0
                ? "tw-relative tw-flex tw-w-full tw-flex-col"
                : "tw-absolute tw-inset-0 tw-flex tw-w-full tw-h-full tw-flex-col"
            }
            aria-hidden={activeIndex !== index}
            style={{ pointerEvents: activeIndex === index ? "auto" : "none" }}
          >
            {screen}
          </m.div>
        ))}
      </div>
      <div className='tw-flex tw-gap-2 tw-items-center tw-mt-10 tw-max-w-20'>
        {screens.map((_, index) => {
          const isPast = index < activeIndex;
          const isActive = index === activeIndex;

          return (
            <button
              key={index}
              type='button'
              aria-label={t("carousel_go_to_slide", { index: index + 1 })}
              onClick={() => goToScreen(index)}
              className='tw-h-1 tw-flex-1 tw-rounded-full tw-bg-white/30 tw-overflow-hidden tw-relative tw-outline-none focus-visible:tw-outline-focus'
            >
              <m.div
                key={isActive ? `active-${slideStartedAt}` : `bar-${index}`}
                className='tw-absolute tw-inset-0 tw-bg-white tw-rounded-full tw-origin-left'
                initial={{ scaleX: isPast ? 1 : 0 }}
                animate={{ scaleX: isPast || isActive ? 1 : 0 }}
                transition={{
                  duration: isActive ? CYCLE_INTERVAL / 1000 : 0,
                  ease: isActive ? "linear" : [0.4, 0, 0.2, 1],
                }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
